import React, { useCallback, useContext, useState, useEffect } from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";

import { ActionBar, Sticky } from "@plex/react-components";

import { INotification, notification } from "infrastructure/notification";
import { ActionBarLink, DialogNavigationContext } from "ui/app/navigation";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { AssigneePlexTranslationDialog } from "../assignee-dialog";
import CommentPlexTranslationDialog from "../comment-dialog/CommentPlexTranslationDialog";
import { plexTranslationMessagesActions } from "../PlexTranslationMessages.actions";
import { IPlexTranslationMessagesManagerState, IPlexTranslationMessageRow, IButtonDisabled } from "../PlexTranslationMessages.state";
import { PlexTranslationUpload, PlexTranslationUploadResult } from "../upload";

import { useCurrentAssignee } from "./PlexTranslationMessagesActionBar.hooks";

export interface IMessagesActionBarProps {
  downloadPlexTranslationUploadTemplate?: () => void;
  onPlexTranslationUploadCompleted?: (notification: INotification) => void;
  exportPlexTranslationsToXml?: () => void;
  requestPlexTranslations?: () => void;
  reassignPlexTranslations?: () => void;
  reviewPlexTranslations?: () => void;
  approvePlexTranslations?: () => void;
  proposePlexTranslations?: () => void;
  rejectPlexTranslations?: () => void;
  obsoletePlexTranslations?: () => void;
  isExportDisabled?: boolean;
  selectedRows?: IPlexTranslationMessageRow[];
}

export const PlexTranslationMessagesManagerActionBar: React.FC<IMessagesActionBarProps> = ({
  downloadPlexTranslationUploadTemplate,
  onPlexTranslationUploadCompleted,
  exportPlexTranslationsToXml,
  requestPlexTranslations,
  reassignPlexTranslations,
  reviewPlexTranslations,
  approvePlexTranslations,
  proposePlexTranslations,
  rejectPlexTranslations,
  obsoletePlexTranslations,
  isExportDisabled,
  selectedRows
}) => {
  const proposedStatus = displayMessages.option("translationStatusProposed").value;
  const inUseStatus = displayMessages.option("translationStatusInUse").value;
  const requestedStatus = displayMessages.option("translationStatusRequested").value;
  const localizedValues = useLocalizedValues(() => ({
    plexTranslationReassignAction: displayMessages.action("plexTranslationsReassign"),
    plexTranslationRejectAction: displayMessages.action("plexTranslationsReject"),
    plexTranslationRequestAction: displayMessages.action("plexTranslationsRequest"),
    plexTranslationReviewAction: displayMessages.action("plexTranslationsReview"),
    plexTranslationProposeAction: displayMessages.action("plexTranslationsPropose"),
    plexTranslationApproveAction: displayMessages.action("plexTranslationsApprove"),
    plexTranslationsObsoleteAction: displayMessages.action("plexTranslationsObsolete"),
    plexTranslationReassignTitle: displayMessages.title("plexTranslationsReassign"),
    plexTranslationRejectTitle: displayMessages.title("plexTranslationsReject"),
    plexTranslationRequestTitle: displayMessages.title("plexTranslationsRequest"),
    plexTranslationReviewTitle: displayMessages.action("plexTranslationsReview"),
    exportPlexTranslationAction: displayMessages.action("exportPlexTranslation"),
    plexTranslationUploadAction: displayMessages.action("plexTranslationsUpload"),
    selectPlexTranslationsUploadFileAction: displayMessages.action("selectPlexTranslationsUploadFile"),
    downloadPlexTranslationFileTemplateAction: displayMessages.action("downloadPlexTranslationFileTemplate"),
    plexTranslationUploadTitle: displayMessages.title("plexTranslationsUpload")
  }));

  const dialogNavigationContext = useContext(DialogNavigationContext);
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const exportExecute = useCallback(() => {
    exportPlexTranslationsToXml?.();
  }, [exportPlexTranslationsToXml]);

  const downloadXmlTemplateExecute = useCallback(() => {
    downloadPlexTranslationUploadTemplate?.();
  }, [downloadPlexTranslationUploadTemplate]);

  const proposeExecute = useCallback(() => {
    proposePlexTranslations?.();
  }, [proposePlexTranslations]);

  const approveExecute = useCallback(() => {
    approvePlexTranslations?.();
  }, [approvePlexTranslations]);

  const obsoleteExecute = useCallback(() => {
    obsoletePlexTranslations?.();
  }, [obsoletePlexTranslations]);

  const getButtonStatus = useCallback(
    () => ({
      isRequestDisabled:
        !selectedRows ||
        selectedRows.length === 0 ||
        selectedRows.some(s => (s.data.translationStatus !== "" && s.data.translationStatus !== inUseStatus) || s.isTranslationChanged),
      isReassignDisabled:
        !selectedRows ||
        selectedRows.length === 0 ||
        selectedRows.some(s => (s.data.translationStatus !== requestedStatus && s.data.translationStatus !== proposedStatus) || s.isTranslationChanged),
      isProposedDisabled: !selectedRows || selectedRows.length === 0 || selectedRows.some(s => s.data.translationStatus !== requestedStatus || s.isTranslationChanged),
      isReviewDisabled: !selectedRows || selectedRows.length === 0 || selectedRows.some(s => s.data.translationStatus !== proposedStatus || s.isTranslationChanged),
      isApproveDisabled: !selectedRows || selectedRows.length === 0 || selectedRows.some(s => s.data.translationStatus !== proposedStatus || s.isTranslationChanged),
      isRejectDisabled: !selectedRows || selectedRows.length === 0 || selectedRows.some(s => s.data.translationStatus !== proposedStatus || s.isTranslationChanged),
      isObsoleteDisabled: !selectedRows || selectedRows.length === 0 || selectedRows.some(s => s.data.translationStatus !== inUseStatus || s.isTranslationChanged)
    }),
    [proposedStatus, inUseStatus, requestedStatus, selectedRows]
  );

  const [button, setButtonState] = useState<IButtonDisabled>(() => ({
    ...getButtonStatus()
  }));

  useEffect(() => {
    setButtonState(() => ({
      ...getButtonStatus()
    }));
  }, [getButtonStatus]);

  const currentAssignee = useCurrentAssignee(selectedRows);

  return (
    <Sticky>
      <ActionBar>
        {roles.PlexLocalizationManager && (
          <ActionBarLink
            text={localizedValues.plexTranslationRequestAction}
            title={localizedValues.plexTranslationRequestAction}
            dialog={{
              content: <AssigneePlexTranslationDialog actionPlexTranslations={requestPlexTranslations} dialogTitle={localizedValues.plexTranslationRequestTitle} />
            }}
            disabled={button.isRequestDisabled}
          />
        )}
        {roles.Translator && (
          <ActionBar.Button
            text={localizedValues.plexTranslationProposeAction}
            title={localizedValues.plexTranslationProposeAction}
            onClick={proposeExecute}
            disabled={button.isProposedDisabled}
          />
        )}
        {roles.PlexLocalizationManager && (
          <ActionBarLink
            text={localizedValues.plexTranslationReassignAction}
            title={localizedValues.plexTranslationReassignAction}
            dialog={{
              content: (
                <AssigneePlexTranslationDialog
                  actionPlexTranslations={reassignPlexTranslations}
                  currentAssignee={currentAssignee}
                  dialogTitle={localizedValues.plexTranslationReassignTitle}
                />
              )
            }}
            disabled={button.isReassignDisabled}
          />
        )}
        {roles.PlexLocalizationManager && (
          <ActionBarLink
            text={localizedValues.plexTranslationReviewAction}
            title={localizedValues.plexTranslationReviewAction}
            dialog={{
              content: <CommentPlexTranslationDialog submitComment={reviewPlexTranslations} dialogTitle={localizedValues.plexTranslationReviewTitle} />
            }}
            disabled={button.isReviewDisabled}
          />
        )}
        {roles.PlexLocalizationManager && (
          <ActionBar.Button
            text={localizedValues.plexTranslationApproveAction}
            title={localizedValues.plexTranslationApproveAction}
            onClick={approveExecute}
            disabled={button.isApproveDisabled}
          />
        )}
        {roles.PlexLocalizationManager && (
          <ActionBarLink
            text={localizedValues.plexTranslationRejectAction}
            title={localizedValues.plexTranslationRejectAction}
            dialog={{
              content: (
                <AssigneePlexTranslationDialog
                  actionPlexTranslations={rejectPlexTranslations}
                  currentAssignee={currentAssignee}
                  dialogTitle={localizedValues.plexTranslationRejectTitle}
                />
              )
            }}
            disabled={button.isRejectDisabled}
          />
        )}
        {(roles.PlexLocalizationManager || roles.LocalizationManager) && (
          <ActionBar.Button
            text={localizedValues.plexTranslationsObsoleteAction}
            title={localizedValues.plexTranslationsObsoleteAction}
            onClick={obsoleteExecute}
            disabled={button.isObsoleteDisabled}
          />
        )}

        {(roles.PlexLocalizationManager || roles.LocalizationManager) && (
          <ActionBar.Dropdown>
            <ActionBar.DropdownButton text={localizedValues.plexTranslationUploadAction} title={localizedValues.plexTranslationUploadTitle} />
            <ActionBar.DropdownItems>
              <ActionBarLink
                text={localizedValues.selectPlexTranslationsUploadFileAction}
                title={localizedValues.selectPlexTranslationsUploadFileAction}
                dialog={{
                  content: (
                    <PlexTranslationUpload
                      onUploadCompleted={error => {
                        if (onPlexTranslationUploadCompleted) {
                          if (error) {
                            dialogNavigationContext.showDialog({
                              content: <PlexTranslationUploadResult />
                            });
                          } else {
                            onPlexTranslationUploadCompleted(notification.success().useMessage({ messageId: displayMessages.info("plexTranslationsUploadCompleted") }));
                          }
                        }
                      }}
                    />
                  )
                }}
              />
              <ActionBar.Divider />
              <ActionBar.Button
                text={localizedValues.downloadPlexTranslationFileTemplateAction}
                title={localizedValues.downloadPlexTranslationFileTemplateAction}
                onClick={downloadXmlTemplateExecute}
              />
            </ActionBar.DropdownItems>
          </ActionBar.Dropdown>
        )}

        <ActionBar.Button
          text={localizedValues.exportPlexTranslationAction}
          title={localizedValues.exportPlexTranslationAction}
          onClick={exportExecute}
          disabled={isExportDisabled}
        />
      </ActionBar>
    </Sticky>
  );
};

export default connect(
  (state: { plexTranslationMessagesManager: IPlexTranslationMessagesManagerState }) => {
    return {
      isExportDisabled: state.plexTranslationMessagesManager.isExportDisabled,
      selectedRows: state.plexTranslationMessagesManager.grid.selected
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        exportPlexTranslationsToXml: plexTranslationMessagesActions.exportPlexTranslationsToXml,
        onPlexTranslationUploadCompleted: plexTranslationMessagesActions.onPlexTranslationUploadCompleted,
        downloadPlexTranslationUploadTemplate: plexTranslationMessagesActions.downloadPlexTranslationUploadTemplate,
        requestPlexTranslations: plexTranslationMessagesActions.requestPlexTranslations,
        reassignPlexTranslations: plexTranslationMessagesActions.reassignPlexTranslations,
        reviewPlexTranslations: plexTranslationMessagesActions.reviewPlexTranslations,
        approvePlexTranslations: plexTranslationMessagesActions.approvePlexTranslations,
        proposePlexTranslations: plexTranslationMessagesActions.proposePlexTranslations,
        rejectPlexTranslations: plexTranslationMessagesActions.rejectPlexTranslations,
        obsoletePlexTranslations: plexTranslationMessagesActions.obsoletePlexTranslations
      },
      dispatch
    );
  }
)(PlexTranslationMessagesManagerActionBar);
